/* Map to show rasters, their legends, animation etc */

/* See wrapper components scenario-rasters-map and bulk-export-rasters-map
   that send the necessary props */

import { Box, BoxProps, Flex } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { Position } from "geojson";
import { Fragment, ReactNode, useContext, useState } from "react";
import { useIntl } from "react-intl";
import Map, { Layer, Marker, NavigationControl, Popup, Source } from "react-map-gl/maplibre";

import { getRasterPointValue, useFrontendSettings } from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { useRasterAnimation } from "@nens/ldo/hooks";
import { useDelayedHoverLocation } from "@nens/ldo/hooks";
import { Raster, RasterStyle, getObservationTypeUnit } from "@nens/lizard/api-v4";
import { useLocalStorage } from "@nens/react/hooks";
import { AnimatedLizardRasterLayer } from "@nens/react-map-gl/components";
import { getWmsRequest, useMaptilerStyleJson } from "@nens/react-map-gl/utils";

import { AnimationControl } from "../animation-control/animation-control";
import { LayerSwitcher } from "../layer-switcher/layer-switcher";
import { Legend } from "../legend/legend";
import { RasterLayer } from "../raster-layer/raster-layer";

interface RastersMapProps extends BoxProps {
  scenarioId?: number /* For the raster point value query */;
  rasters?: {
    rasterType: string;
    description: string;
    raster: Raster;
  }[];
  wmsLayers?: {
    name: string;
    endpoint: string;
    layer: string;
  }[];
  markers: Position[];
  mapId: string;
  size: "s" | "l";
  extraButtons?: ReactNode[];
}

export function RastersMap({
  rasters = [],
  wmsLayers = [],
  markers,
  mapId,
  size,
  extraButtons = [],
  scenarioId,
  ...rest
}: RastersMapProps) {
  const intl = useIntl();
  const [currentLayer, actualSetCurrentLayer] = useState<string | null>(null);
  const [currentBaselayer, setCurrentBaselayer] = useLocalStorage<string | null>(
    "baselayer",
    "basic"
  );
  const raster = rasters.find((raster) => raster.rasterType === currentLayer)?.raster ?? null;
  const wmsLayer = wmsLayers.find((wmsLayer) => wmsLayer.layer === currentLayer) ?? null;

  const animationState = useRasterAnimation(raster);

  const [editedRasterStyle, setEditedRasterStyle] = useState<string | null>(null);
  const [rasterMin, setRasterMin] = useState<number | null>(null);
  const [rasterMax, setRasterMax] = useState<number | null>(null);

  const setCurrentRasterLayer = (layer: string | null) => {
    actualSetCurrentLayer(layer);
    setRasterMin(null);
    setRasterMax(null);
  };

  const rasterStyle = raster
    ? new RasterStyle(raster, rasterMin, rasterMax, editedRasterStyle)
    : null;

  const { mapLibreTransformFunction } = useContext(LdoAuthContext);
  const frontendSettings = useFrontendSettings();
  const style = useMaptilerStyleJson(
    currentBaselayer,
    frontendSettings.isSuccess ? frontendSettings.data.maptiler_api_key : null,
    false
  );

  const { onHover, onMouseOut, currentLocation } = useDelayedHoverLocation();

  const rasterPointResponse = useQuery(
    getRasterPointValue(
      scenarioId ?? -1,
      raster && !raster.temporal ? raster.uuid : "", // Only use the raster uuid if it's not a temporal raster
      currentLocation?.lng ?? null,
      currentLocation?.lat ?? null
    )
  );
  const value = rasterPointResponse.isSuccess ? rasterPointResponse.data : null;
  const unit = getObservationTypeUnit(raster?.observation_type ?? null);

  if (!style) return null;

  const mapMarkers = markers.map((marker, index) => (
    <Marker
      key={`marker-${index}`}
      longitude={marker[0]}
      latitude={marker[1]}
      anchor="center"
    ></Marker>
  ));

  const wmsUrl = wmsLayer
    ? getWmsRequest(wmsLayer.endpoint, {
        layer: wmsLayer.layer,
        styles: "generic",
        transparent: true,
      })
    : null;

  return (
    <Box w="full" h="full" id={mapId} {...rest}>
      <Map
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "4px",
          overflow: "hidden",
        }}
        id={mapId}
        mapStyle={style}
        attributionControl={false}
        initialViewState={{
          longitude: markers.length > 0 ? markers[0][0] : 5.4612108,
          latitude: markers.length > 0 ? markers[0][1] : 52,
          zoom: markers.length > 0 ? 15 : 5.5,
        }}
        transformRequest={mapLibreTransformFunction}
        onMouseMove={onHover}
        onMouseOut={onMouseOut}
      >
        <Layer id="empty-layer-1" type="background" layout={{ visibility: "none" }} />
        {raster && rasterStyle && !raster.temporal && !animationState.valid ? (
          <RasterLayer raster={raster} rasterStyle={rasterStyle} />
        ) : null}
        {raster && animationState.valid ? (
          <AnimatedLizardRasterLayer
            isAnimating={animationState.animating}
            raster={raster}
            rasterStyle={rasterStyle}
            wmsBaseUrl="https://ldo.lizard.net/wms/"
            startMs={animationState.startMs}
            endMs={animationState.endMs}
            currentTime={animationState.currentTime}
            setCurrentTime={animationState.setCurrentTime}
            setWaiting={animationState.setWaiting}
            opacity={0.7}
            beforeId="empty-layer-1"
          />
        ) : null}
        {wmsLayer && wmsUrl ? (
          <Source
            key={wmsLayer.endpoint + wmsLayer.layer}
            id={"source-" + wmsLayer.endpoint + wmsLayer.layer}
            type="raster"
            tiles={[wmsUrl]}
            tileSize={256}
          >
            <Layer
              id={wmsLayer.endpoint + wmsLayer.layer}
              minzoom={0}
              maxzoom={22}
              type="raster"
              paint={{ "raster-opacity": 1 }}
              beforeId="empty-layer-1"
            />
          </Source>
        ) : null}
        {mapMarkers}
        {currentLocation !== null && value !== null ? (
          <Popup longitude={currentLocation.lng} latitude={currentLocation.lat} closeButton={false}>
            {value.toFixed(2)} {unit}
          </Popup>
        ) : null}
        <NavigationControl position="top-right" />
      </Map>
      <LayerSwitcher
        layers={[
          ...rasters.map((raster) => ({
            layer: raster.rasterType,
            description: raster.description,
          })),
          ...wmsLayers.map((wmsLayer) => ({ layer: wmsLayer.layer, description: wmsLayer.name })),
        ]}
        currentLayer={currentLayer}
        setLayer={setCurrentRasterLayer}
        baselayers={[
          {
            key: "basic",
            description: intl.formatMessage({
              id: "layerswitcher.topographic",
              defaultMessage: "Topographic",
            }),
          },
          {
            key: "979c501d-dd64-478a-a7bf-960515d5c0d2",
            description: intl.formatMessage({
              id: "layerswitcher.satellite",
              defaultMessage: "Satellite",
            }),
          },
          {
            key: "d481b712-934d-485d-bfdb-bc1abf01fd5c",
            description: intl.formatMessage({
              id: "layerswitcher.light",
              defaultMessage: "Light",
            }),
          },
          {
            key: "6d99d4a9-cae0-437a-a87b-bb78e2c85094",
            description: intl.formatMessage({
              id: "layerswitcher.dark",
              defaultMessage: "Dark",
            }),
          },
        ]}
        currentBaselayer={currentBaselayer}
        setBaselayer={(layer) => setCurrentBaselayer(layer)}
      />
      {raster && rasterStyle ? (
        <Legend
          layer={raster.wms_info.layer}
          unit={getObservationTypeUnit(raster.observation_type)}
          name={raster.name}
          rasterStyle={rasterStyle}
          setRasterMin={setRasterMin}
          setRasterMax={setRasterMax}
          setRasterStyle={setEditedRasterStyle}
          width="190px"
        />
      ) : null}
      {animationState.valid ? (
        <AnimationControl
          currentTime={animationState.currentTime}
          waiting={animationState.waiting}
          animating={animationState.animating}
          start={animationState.start}
          stop={animationState.stop}
          startMs={animationState.startMs}
          endMs={animationState.endMs}
          size={size}
          setCurrentTime={animationState.setCurrentTime}
          extraButtons={extraButtons}
        />
      ) : (
        <Flex flexDirection="row" gap={2} position="absolute" bottom="0" m="8px">
          {extraButtons.map((button, index) => (
            <Fragment key={`button-${index}`}>{button}</Fragment>
          ))}
        </Flex>
      )}
    </Box>
  );
}
